import React, { useEffect, useState, createContext, useContext } from 'react';
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
  isLoading: boolean;
}
interface User {
  name: string;
  email: string;
  role: string;
  isAdmin: boolean;
}
// Demo users for the app
const DEMO_USERS = [{
  email: 'testemail@playbooks.com',
  password: 'example1',
  name: 'Alex Johnson',
  role: 'Senior Software Engineer',
  isAdmin: true
}, {
  email: 'user@example.com',
  password: 'password123',
  name: 'Sam Wilson',
  role: 'Software Developer',
  isAdmin: false
}];
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    // Check for saved auth state on load
    const savedUser = localStorage.getItem('growthPlatformUser');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('growthPlatformUser');
      }
    }
    setIsInitialized(true);
  }, []);
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    // Simulate network request
    await new Promise(resolve => setTimeout(resolve, 1000));
    const foundUser = DEMO_USERS.find(u => u.email === email && u.password === password);
    if (foundUser) {
      const {
        password,
        ...userWithoutPassword
      } = foundUser;
      setUser(userWithoutPassword);
      localStorage.setItem('growthPlatformUser', JSON.stringify(userWithoutPassword));
      setIsLoading(false);
      return true;
    } else {
      setError('Invalid email or password');
      setIsLoading(false);
      return false;
    }
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('growthPlatformUser');
  };
  return <AuthContext.Provider value={{
    isAuthenticated: !!user,
    user,
    login,
    logout,
    error,
    isLoading
  }} data-id="element-1208">
      {isInitialized ? children : <div data-id="element-1209">Loading...</div>}
    </AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};